import {
  Assignment,
  AttachMoney,
  Calculate,
  ShoppingCart,
} from '@mui/icons-material';
import { Box, FormControl, Grid, MenuItem, Select } from '@mui/material';
import { getSupplierStats } from 'lib/api/publicMarket';
import TableCard from 'lib/designSystem/Card/TableCard';
import BarChart from 'lib/designSystem/Charts/BarChart';
import LineChart from 'lib/designSystem/Charts/LineChart';
import StatCard from 'lib/designSystem/Charts/StatCard';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import SubtitleWithTooltip from 'lib/designSystem/SubtitleWithTooltip';
import CustomToggleButtonGroup from 'lib/designSystem/ToggleButtonGroup';
import { BASE_COLORS, CHART_COLORS } from 'lib/functions/const/colors';
import { formatMoney } from 'lib/functions/utils';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

type ChartType = 'tenders' | 'orders';

const TendersStats = ({ id }: { id?: string | null }) => {
  const dispatch = useDispatch();
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [amountChartType, setAmountChartType] = useState<ChartType>('tenders');
  const [countChartType, setCountChartType] = useState<ChartType>('tenders');
  const [buyersChartType, setBuyersChartType] = useState<ChartType>('tenders');
  const [categoriesChartType, setCategoriesChartType] =
    useState<ChartType>('tenders');

  if (!id) throw new Error('Supplier ID is required');

  const query = selectedYear ? `year=${selectedYear}` : '';
  const { data, isPending } = getSupplierStats(dispatch, id, query);

  if (isPending) return <CenteredCircularProgress type="layout" />;

  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const colors = Object.values(CHART_COLORS).slice(2);

  const handleYearChange = (event: { target: { value: unknown } }) => {
    setSelectedYear(event.target.value as number);
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Box sx={{ position: 'absolute', top: -36, right: 0 }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select value={selectedYear} onChange={handleYearChange} displayEmpty>
            {years.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={2} my={1}>
        <Grid item xs={2.5}>
          <StatCard
            label="Cantidad de licitaciones"
            value={data?.statistics.tendersCount || 0}
            valueLabel=""
            color={BASE_COLORS.blue}
            icon={<Assignment sx={{ fontSize: 24 }} />}
          />
        </Grid>
        <Grid item xs={3.5}>
          <StatCard
            label="Monto adjudicado"
            value={formatMoney(data?.statistics.adjudicatedAmount || 0)}
            valueLabel="CLP"
            color={BASE_COLORS.purple}
            icon={<AttachMoney sx={{ fontSize: 24 }} />}
          />
        </Grid>
        <Grid item xs={2.5}>
          <StatCard
            label="Cantidad de ordenes"
            value={data?.statistics.purchaseOrdersCount || 0}
            valueLabel=""
            color={BASE_COLORS.green}
            icon={<ShoppingCart sx={{ fontSize: 24 }} />}
          />
        </Grid>
        <Grid item xs={3.5}>
          <StatCard
            label="Monto ordenes de compra"
            value={formatMoney(data?.statistics.purchaseOrdersAmount || 0)}
            valueLabel="CLP"
            color={BASE_COLORS.orange}
            icon={<Calculate sx={{ fontSize: 24 }} />}
          />
        </Grid>

        <Grid item xs={12}>
          <TableCard>
            <SubtitleWithTooltip
              title="Monto en el tiempo"
              tooltipContent="Evolución del monto a lo largo del tiempo"
              tooltipPlacement="right"
              variant="compact"
            >
              <CustomToggleButtonGroup
                value={amountChartType}
                onChange={(value) =>
                  setAmountChartType(value as 'tenders' | 'orders')
                }
                options={[
                  { value: 'tenders', label: 'Licitaciones' },
                  { value: 'orders', label: 'Ordenes' },
                ]}
              />
            </SubtitleWithTooltip>
            <LineChart
              key={`${amountChartType}AmountLineChart`}
              data={
                amountChartType === 'tenders'
                  ? data?.tenderAmountLineChartData || []
                  : data?.purchaseOrderAmountLineChartData || []
              }
              colors={colors}
              showArea={false}
            />
          </TableCard>
        </Grid>

        <Grid item xs={12}>
          <TableCard>
            <SubtitleWithTooltip
              title="Cantidad en el tiempo"
              tooltipContent="Cantidad a lo largo del tiempo"
              tooltipPlacement="right"
              variant="compact"
            >
              <CustomToggleButtonGroup
                value={countChartType}
                onChange={(value) =>
                  setCountChartType(value as 'tenders' | 'orders')
                }
                options={[
                  { value: 'tenders', label: 'Licitaciones' },
                  { value: 'orders', label: 'Ordenes' },
                ]}
              />
            </SubtitleWithTooltip>
            <BarChart
              data={
                countChartType === 'tenders'
                  ? data?.tenderCountBarChartData || []
                  : data?.purchaseOrderCountBarChartData || []
              }
              direction="vertical"
              height={240}
              yAxisFormat="auto"
              colors={colors}
            />
          </TableCard>
        </Grid>

        <Grid item xs={6}>
          <TableCard>
            <SubtitleWithTooltip
              title="Top 10 compradores"
              tooltipContent="Top 10 compradores por monto"
              tooltipPlacement="right"
              variant="compact"
            >
              <CustomToggleButtonGroup
                value={buyersChartType}
                onChange={(value) =>
                  setBuyersChartType(value as 'tenders' | 'orders')
                }
                options={[
                  { value: 'tenders', label: 'Licitaciones' },
                  { value: 'orders', label: 'Ordenes' },
                ]}
              />
            </SubtitleWithTooltip>
            <BarChart
              data={
                buyersChartType === 'tenders'
                  ? data?.tenderTop10Buyers || []
                  : data?.purchaseOrderTop10Buyers || []
              }
              direction="horizontal"
              height={350}
              yAxisFormat="auto"
              colors={[
                buyersChartType === 'tenders'
                  ? CHART_COLORS.blue
                  : CHART_COLORS.orange,
              ]}
            />
          </TableCard>
        </Grid>

        <Grid item xs={6}>
          <TableCard>
            <SubtitleWithTooltip
              title="Top 10 categorías"
              tooltipContent="Top 10 categorías por monto"
              tooltipPlacement="right"
              variant="compact"
            >
              <CustomToggleButtonGroup
                value={categoriesChartType}
                onChange={(value) =>
                  setCategoriesChartType(value as 'tenders' | 'orders')
                }
                options={[
                  { value: 'tenders', label: 'Licitaciones' },
                  { value: 'orders', label: 'Ordenes' },
                ]}
              />
            </SubtitleWithTooltip>
            <BarChart
              data={
                categoriesChartType === 'tenders'
                  ? data?.tenderTop10Categories || []
                  : data?.purchaseOrderTop10Categories || []
              }
              direction="horizontal"
              height={350}
              yAxisFormat="auto"
              colors={[
                categoriesChartType === 'tenders'
                  ? CHART_COLORS.blue
                  : CHART_COLORS.orange,
              ]}
            />
          </TableCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TendersStats;
